import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { Language, StartConversationResponse } from '@shared/types';
import { LanguageSelector, VisitorForm } from '../components';

/**
 * HomePage Component
 * 
 * Landing page for visitors to start a new conversation.
 * Combines language selection and visitor registration form.
 * 
 * User Story 1: Language selection and visitor registration
 * - FR-002: Multi-language support
 * - FR-004: Visitor information collection
 * - FR-026: Rate limiting enforcement
 */

export const HomePage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  
  const [selectedLanguage, setSelectedLanguage] = useState<Language>(
    i18n.language as Language
  );

  /**
   * Handles successful conversation start
   * Navigates to the conversation page with the conversationId
   */
  const handleConversationStart = (response: StartConversationResponse) => {
    // Navigate to conversation page
    navigate(`/conversation/${response.conversationId}`, {
      state: {
        conversationId: response.conversationId,
        visitorId: response.visitorId,
        firstQuestion: response.firstQuestion,
        language: response.language,
      },
    });
  };

  /**
   * Handles language change from LanguageSelector
   */
  const handleLanguageChange = (language: Language) => {
    setSelectedLanguage(language);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      {/* Header */}
      <header className="py-6 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <LanguageSelector
            className="justify-end"
            onLanguageChange={handleLanguageChange}
          />
        </div>
      </header>

      {/* Main Content */}
      <main className="px-4 sm:px-6 lg:px-8 py-12">
        <div className="max-w-2xl mx-auto">
          {/* Hero Section */}
          <div className="text-center mb-12">
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-white mb-4">
              {t('homePage.title')}
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-300 mb-2">
              {t('homePage.subtitle')}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('homePage.description')}
            </p>
          </div>

          {/* Visitor Registration Form */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 sm:p-10">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">
              {t('homePage.formTitle')}
            </h2>
            
            <VisitorForm
              language={selectedLanguage}
              onConversationStart={handleConversationStart}
            />
          </div>

          {/* Features Section */}
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-6">
            {/* Feature 1: AI-Powered */}
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900 mb-4">
                <svg
                  className="w-6 h-6 text-blue-600 dark:text-blue-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                  />
                </svg>
              </div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                {t('homePage.features.aiPowered.title')}
              </h3>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                {t('homePage.features.aiPowered.description')}
              </p>
            </div>

            {/* Feature 2: Confidential */}
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-green-100 dark:bg-green-900 mb-4">
                <svg
                  className="w-6 h-6 text-green-600 dark:text-green-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                  />
                </svg>
              </div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                {t('homePage.features.confidential.title')}
              </h3>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                {t('homePage.features.confidential.description')}
              </p>
            </div>

            {/* Feature 3: Fast */}
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-purple-100 dark:bg-purple-900 mb-4">
                <svg
                  className="w-6 h-6 text-purple-600 dark:text-purple-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
              </div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                {t('homePage.features.fast.title')}
              </h3>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                {t('homePage.features.fast.description')}
              </p>
            </div>
          </div>

          {/* Footer Note */}
          <p className="mt-12 text-center text-xs text-gray-500 dark:text-gray-400">
            {t('homePage.footerNote')}
          </p>
        </div>
      </main>
    </div>
  );
};
